import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Compass, Ticket, Check } from 'lucide-react';
import SocialLoginButtons from '../components/SocialLoginButtons';

const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register, becomeOrganizer } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const intent = searchParams.get('intent');
  const [role, setRole] = useState(intent === 'publish' ? 'organizer' : 'user');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await register(email, password, name);
      if (role === 'organizer') {
        try {
          await becomeOrganizer();
        } catch (upgradeErr) {
        }
      }
      navigate(role === 'organizer' ? '/organizador' : '/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al registrarse');
    } finally {
      setLoading(false);
    }
  };

  const RoleCard = ({ value, icon: Icon, title, description, testId }) => {
    const selected = role === value;
    return (
      <button
        type="button"
        onClick={() => setRole(value)}
        className={`text-left w-full p-4 rounded-lg border-2 transition-all ${
          selected
            ? 'border-[#0A0A0A] bg-[#0A0A0A] text-white shadow-lg'
            : 'border-[#E5E5E5] bg-white text-[#0A0A0A] hover:border-[#0A0A0A]/40'
        }`}
        data-testid={testId}
        aria-pressed={selected}
      >
        <div className="flex items-start gap-3">
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
              selected ? 'bg-primary text-white' : 'bg-[#F5F5F5] text-[#0A0A0A]'
            }`}
          >
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <p className={`font-heading font-bold ${selected ? 'text-white' : 'text-[#0A0A0A]'}`}>{title}</p>
              {selected && <Check className="w-4 h-4 text-primary" />}
            </div>
            <p className={`text-xs mt-1 ${selected ? 'text-white/80' : 'text-[#737373]'}`}>{description}</p>
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{background: 'linear-gradient(135deg, #FAFAFA 0%, #FFF7ED 100%)'}}>
      <Card className="w-full max-w-md bg-white border-2 border-[#0A0A0A] shadow-2xl" data-testid="register-card">
        <CardHeader className="space-y-2">
          <div className="inline-flex items-center gap-1 self-start bg-[#0A0A0A] text-white text-xs font-semibold px-2 py-1 rounded">
            SALTA · NOA
          </div>
          <CardTitle className="text-3xl font-heading font-bold text-[#0A0A0A]">
            Únete a Mi Tribu
          </CardTitle>
          <CardDescription className="text-[#525252]">
            Elegí para qué querés usar Mi Tribu. Podés cambiar después.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 mb-6" data-testid="role-selector">
            <RoleCard
              value="user"
              icon={Ticket}
              title="Quiero descubrir eventos"
              description="Recibí RSVP, comprá entradas y seguí a tus organizadores favoritos."
              testId="role-user"
            />
            <RoleCard
              value="organizer"
              icon={Compass}
              title="Quiero publicar eventos"
              description="Publicá tus eventos, vendé entradas y accedé a analytics de tu tribu."
              testId="role-organizer"
            />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#0A0A0A] font-medium">Nombre completo</Label>
              <Input
                id="name"
                type="text"
                placeholder="Tu nombre"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="bg-white border-[#0A0A0A]/20 text-[#0A0A0A] focus:border-[#0A0A0A]"
                data-testid="name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#0A0A0A] font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-white border-[#0A0A0A]/20 text-[#0A0A0A] focus:border-[#0A0A0A]"
                data-testid="email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#0A0A0A] font-medium">Contraseña</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="bg-white border-[#0A0A0A]/20 text-[#0A0A0A] focus:border-[#0A0A0A]"
                data-testid="password-input"
              />
              <p className="text-xs text-[#737373]">Mínimo 8 caracteres</p>
            </div>
            {error && (
              <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3" data-testid="error-message">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              className={
                role === 'organizer'
                  ? 'w-full bg-[#0A0A0A] hover:bg-[#0A0A0A]/90 text-white font-semibold'
                  : 'w-full bg-primary hover:bg-primary/90 text-white font-semibold'
              }
              data-testid="register-button"
            >
              {loading
                ? 'Registrando...'
                : role === 'organizer'
                ? 'Crear cuenta y publicar'
                : 'Crear cuenta'}
            </Button>
          </form>

          <SocialLoginButtons label="O registrate con" />

          <p className="text-center text-sm text-[#525252] mt-6">
            ¿Ya tienes cuenta?{' '}
            <Link to="/login" className="text-[#0A0A0A] font-semibold hover:underline" data-testid="login-link">
              Inicia sesión
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
